import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n.jsx";
import { PageHeader, Empty } from "../components/Bits";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { History } from "lucide-react";

export default function Norms() {
  const { t, tn } = useI18n();
  const { can } = useAuth();
  const [works, setWorks] = useState([]);
  const [units, setUnits] = useState([]);
  const [norms, setNorms] = useState([]);
  const [prices, setPrices] = useState([]);
  const [modal, setModal] = useState(null); // {kind, work}
  const [val, setVal] = useState("");
  const [from, setFrom] = useState("2026-06-01");

  const load = async () => {
    const [w, u, n, p] = await Promise.all([
      api.get("/config/work-types"), api.get("/config/units"),
      api.get("/norms").catch(() => ({ data: [] })), api.get("/prices").catch(() => ({ data: [] })),
    ]);
    setWorks(w.data); setUnits(u.data); setNorms(n.data); setPrices(p.data);
  };
  useEffect(() => { load(); }, []);

  const cur = (arr, wid) => arr.find((x) => x.work_type_id === wid && !x.effective_to);
  const openModal = (kind, work) => { setModal({ kind, work }); setVal(""); };
  const save = async () => {
    try {
      if (modal.kind === "norm") await api.post("/norms", { work_type_id: modal.work.id, norm_minutes_per_unit: val, effective_from: from });
      else await api.post("/prices", { work_type_id: modal.work.id, price_per_unit: val, effective_from: from });
      toast.success(t("save")); setModal(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || t("error")); }
  };

  return (
    <div>
      <PageHeader title={t("norms")} subtitle={t("history_note")} />
      {works.length === 0 ? <Empty /> : (
        <div className="tactical-card overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead><tr className="border-b text-left text-[var(--text-3)]">
              <th className="p-3">{t("work")}</th><th className="p-3">{t("unit")}</th>
              <th className="p-3 text-right">{t("norm_min_per_unit")}</th><th className="p-3">{t("measurement_type")}</th>
              {can("prices.view") && <th className="p-3 text-right">€/u</th>}
              <th className="p-3"></th>
            </tr></thead>
            <tbody>
              {works.map((w) => {
                const n = cur(norms, w.id), p = cur(prices, w.id);
                return (
                  <tr key={w.id} className="border-b hover:bg-[var(--bg)]">
                    <td className="p-3 font-semibold">{tn(w.name)}</td>
                    <td className="p-3">{tn(units.find((u) => u.id === w.unit_id)?.name)}</td>
                    <td className="p-3 text-right font-mono-num" data-testid={`norm-min-${w.id}`}>{n ? n.norm_minutes_per_unit : "—"}</td>
                    <td className="p-3 text-xs text-[var(--text-3)]">{t(w.measurement_type || "QUANTITY_BASED")} · {t(w.activity_type || "PRODUCTIVE")}</td>
                    {can("prices.view") && <td className="p-3 text-right font-mono-num">{p ? `€${p.price_per_unit}` : "—"}</td>}
                    <td className="p-3 text-right whitespace-nowrap">
                      {can("norms.create") && <button onClick={() => openModal("norm", w)} className="text-[var(--blue)] mr-3" data-testid={`edit-norm-${w.id}`}>{t("norm_hours")}</button>}
                      {can("prices.create") && <button onClick={() => openModal("price", w)} className="text-[var(--amber)]" data-testid={`edit-price-${w.id}`}>€</button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <Dialog open={!!modal} onOpenChange={() => setModal(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{modal?.kind === "norm" ? t("norm_min_per_unit") : t("cost")} — {tn(modal?.work?.name)}</DialogTitle></DialogHeader>
          <p className="text-xs text-[var(--text-3)] flex items-center gap-1"><History size={13} /> {t("old_reports_keep")}</p>
          <Input type="number" step="0.01" value={val} onChange={(e) => setVal(e.target.value)} placeholder={modal?.kind === "norm" ? t("norm_min_per_unit") : "€ / unit"} data-testid="norm-value" />
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="norm-from" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(null)}>{t("cancel")}</Button>
            <Button onClick={save} className="bg-[var(--amber)] hover:bg-[var(--amber-2)] text-white" data-testid="norm-save">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
